import React, { useState } from 'react';
import { Plus, Minus, X, Phone } from 'lucide-react';

const SectorContactForm = ({ sector, initialPhoneNumbers, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    sector: sector || '',
    phoneNumbers: initialPhoneNumbers || []
  });
  const [errors, setErrors] = useState({});

  // Define valid sectors
  const validSectors = ['Job-Portal', 'Funding', 'Ca-Portal', 'Reports'];

  const validate = () => {
    const newErrors = {};
    if (!formData.sector.trim()) newErrors.sector = 'Sector is required';
    
    formData.phoneNumbers.forEach((phone, index) => {
      if (!phone?.trim()) newErrors[`phone-${index}`] = 'Phone number is required';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData.sector, formData.phoneNumbers);
    }
  };

  const handlePhoneChange = (index, value) => {
    const updatedPhoneNumbers = [...formData.phoneNumbers];
    updatedPhoneNumbers[index] = value;
    setFormData({ ...formData, phoneNumbers: updatedPhoneNumbers });
  };

  const addPhoneNumber = () => {
    setFormData({
      ...formData,
      phoneNumbers: [...formData.phoneNumbers, '']
    });
  };

  const removePhoneNumber = (index) => {
    const updatedPhoneNumbers = formData.phoneNumbers.filter((_, i) => i !== index);
    setFormData({ ...formData, phoneNumbers: updatedPhoneNumbers });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b p-4">
          <h2 className="text-xl font-semibold">
            {sector ? `Edit ${sector} Phone Numbers` : 'Add New Sector Phone Numbers'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sector</label>
            <select
              value={formData.sector}
              onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
              disabled={!!sector}
              className={`w-full p-2 border rounded ${errors.sector ? 'border-red-500' : 'border-gray-300'} ${sector ? 'bg-gray-100' : ''}`}
            >
              <option value="">Select a sector</option>
              {validSectors.map((sectorOption) => (
                <option key={sectorOption} value={sectorOption}>
                  {sectorOption}
                </option>
              ))}
            </select>
            {errors.sector && <p className="text-red-500 text-xs mt-1">{errors.sector}</p>}
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium">Phone Numbers</h3>
              <button
                type="button"
                onClick={addPhoneNumber}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
              >
                <Plus size={16} />
                Add Phone Number
              </button>
            </div>

            <div className="space-y-3">
              {formData.phoneNumbers.map((phone, index) => (
                <div key={index} className="border rounded-lg p-3 relative flex items-center">
                  <div className="flex-grow">
                    <label className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <Phone size={16} />
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={phone}
                      onChange={(e) => handlePhoneChange(index, e.target.value)}
                      className={`w-full p-2 border rounded ${errors[`phone-${index}`] ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="Enter phone number"
                    />
                    {errors[`phone-${index}`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`phone-${index}`]}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removePhoneNumber(index)}
                    className="ml-2 text-gray-400 hover:text-red-500 mt-6"
                    title="Remove phone number"
                  >
                    <Minus size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save Phone Numbers
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SectorContactForm;